/* eslint
	@typescript-eslint/no-unsafe-call: "off" -- types missing in eslint for exiv2wasm
*/
import { PixelData } from "@/core/utils/image-data"
import { PhotoMetadataRecord } from "@/lib/catalog"
import { IFDEntry } from "@/core/exif/ifd"
import { TagCode } from "@/core/exif/tag-codes"
import { TagType } from "@/core/exif/tag-types"
import { convertDateOffsetToExifString, convertDateToExifString } from "@/utils/exif"
import { encodeApertureValue, encodeShutterSpeedValue, writeJpegExifToBytes } from "../exif/exif"
import { displayP3 } from "../exif/icc-profiles"

export interface RawMetadataEncodeJPEGOptions {
	quality?: number
	ppi?: number
	metadata?: PhotoMetadataRecord
	xmpData?: Record<string, any>
}

export interface EncodedJPEGData {
	bytes: Uint8Array
	type: "image/jpeg"
}

export interface JpegEncoderLike {
	encodeJPEG(pixelData: PixelData, options: RawMetadataEncodeJPEGOptions): Promise<Blob>
	encodeJPEGBytes?(
		pixelData: PixelData,
		options: RawMetadataEncodeJPEGOptions,
	): Promise<EncodedJPEGData>
}

export function encodedJPEGDataToBlob(encodedJPEG: EncodedJPEGData): Blob {
	const { bytes } = encodedJPEG
	const buffer = bytes.buffer

	if (!(buffer instanceof ArrayBuffer)) {
		throw new Error("Encoded JPEG bytes are not backed by an ArrayBuffer")
	}

	return new Blob([new Uint8Array(buffer, bytes.byteOffset, bytes.byteLength)], {
		type: encodedJPEG.type,
	})
}

export const encodeJpeg = async (
	pixelData: PixelData,
	options: RawMetadataEncodeJPEGOptions & {
		jpegEncoder: JpegEncoderLike
	},
) => {
	const encoder = options.jpegEncoder

	if (!encoder) {
		throw new Error("Cannot find JPEG encoder")
	}

	return encoder.encodeJPEG(pixelData, {
		quality: options.quality,
		ppi: options.ppi,
		metadata: options.metadata,
		xmpData: options.xmpData,
	})
}

export const encodeJpegBytes = async (
	pixelData: PixelData,
	options: RawMetadataEncodeJPEGOptions & {
		jpegEncoder: JpegEncoderLike
	},
): Promise<EncodedJPEGData> => {
	const encoder = options.jpegEncoder

	if (!encoder) {
		throw new Error("Cannot find JPEG encoder")
	}

	const encodeOptions = {
		quality: options.quality,
		ppi: options.ppi,
		metadata: options.metadata,
		xmpData: options.xmpData,
	}

	if (encoder.encodeJPEGBytes) {
		return encoder.encodeJPEGBytes(pixelData, encodeOptions)
	}

	const blob = await encoder.encodeJPEG(pixelData, encodeOptions)

	return {
		bytes: new Uint8Array(await blob.arrayBuffer()),
		type: "image/jpeg",
	}
}

const makeJpegMetadata = (
	metadata: PhotoMetadataRecord,
	pixelData: PixelData,
	ppi: number,
	xmpData: Record<string, any>,
) => {
	const { width, height, colorSpace } = pixelData

	const dateModified = convertDateToExifString(new Date())

	const dateCreated = metadata.dateCreated
		? convertDateToExifString(new Date(metadata.dateCreated), metadata.dateCreatedOffset ?? 0)
		: dateModified

	const dateCreatedOffset = metadata.dateCreatedOffset
		? convertDateOffsetToExifString(metadata.dateCreatedOffset)
		: "00:00"

	const textEncoder = new TextEncoder()

	const TIFF: IFDEntry[] = [
		{ tag: TagCode.ImageWidth, type: TagType.LONG, value: width },
		{ tag: TagCode.ImageLength, type: TagType.LONG, value: height },
		{ tag: TagCode.Orientation, type: TagType.SHORT, value: 1 },
		{ tag: TagCode.XResolution, type: TagType.RATIONAL, value: ppi },
		{ tag: TagCode.YResolution, type: TagType.RATIONAL, value: ppi },
		{ tag: TagCode.ResolutionUnit, type: TagType.SHORT, value: 2 },
		{
			tag: TagCode.Software,
			type: TagType.ASCII,
			value: textEncoder.encode("Pixieset Photo Editor"),
		},
		{ tag: TagCode.DateTime, type: TagType.ASCII, value: textEncoder.encode(dateModified) },
	]

	const Exif: IFDEntry[] = [
		{
			tag: TagCode.ExifVersion,
			type: TagType.UNDEFINED,
			value: textEncoder.encode("0231"),
		},
		{ tag: TagCode.PixelXDimension, type: TagType.LONG, value: width },
		{ tag: TagCode.PixelYDimension, type: TagType.LONG, value: height },
		{
			tag: TagCode.DateTimeDigitized,
			type: TagType.ASCII,
			value: textEncoder.encode(dateCreated),
		},
		{
			tag: TagCode.DateTimeOriginal,
			type: TagType.ASCII,
			value: textEncoder.encode(dateCreated),
		},
		{
			tag: TagCode.OffsetTime,
			type: TagType.ASCII,
			value: textEncoder.encode(dateCreatedOffset),
		},
		{
			tag: TagCode.OffsetTimeOriginal,
			type: TagType.ASCII,
			value: textEncoder.encode(dateCreatedOffset),
		},
		{
			tag: TagCode.OffsetTimeDigitized,
			type: TagType.ASCII,
			value: textEncoder.encode(dateCreatedOffset),
		},
	]

	if (metadata.iso) {
		Exif.push({ tag: TagCode.ISOSpeed, type: TagType.LONG, value: metadata.iso })
		Exif.push({ tag: TagCode.ISOSpeedRatings, type: TagType.LONG, value: metadata.iso })
	}

	if (metadata.focalLength) {
		Exif.push({
			tag: TagCode.FocalLength,
			type: TagType.RATIONAL,
			value: metadata.focalLength,
		})
	}

	if (metadata.shutterSpeed) {
		const shutterSpeedValue = encodeShutterSpeedValue(metadata.shutterSpeed)

		Exif.push({
			tag: TagCode.ShutterSpeedValue,
			type: TagType.SRATIONAL,
			value: shutterSpeedValue,
		})

		Exif.push({
			tag: TagCode.ExposureTime,
			type: TagType.RATIONAL,
			value: metadata.shutterSpeed,
		})
	}

	if (metadata.fNumber) {
		const apertureValue = encodeApertureValue(metadata.fNumber)

		Exif.push({ tag: TagCode.ApertureValue, type: TagType.RATIONAL, value: apertureValue })
		Exif.push({ tag: TagCode.FNumber, type: TagType.RATIONAL, value: metadata.fNumber })
	}

	if (metadata.lensMake) {
		Exif.push({
			tag: TagCode.LensMakeExif,
			type: TagType.ASCII,
			value: textEncoder.encode(metadata.lensMake),
		})
	}

	if (metadata.lensModel) {
		Exif.push({
			tag: TagCode.LensModelExif,
			type: TagType.ASCII,
			value: textEncoder.encode(metadata.lensModel),
		})
	}

	if (metadata.flash != null) {
		Exif.push({ tag: TagCode.Flash, type: TagType.SHORT, value: metadata.flash })
	}

	if (metadata.cameraMake) {
		TIFF.push({
			tag: TagCode.Make,
			type: TagType.ASCII,
			value: textEncoder.encode(metadata.cameraMake),
		})
	}

	if (metadata.cameraModel) {
		TIFF.push({
			tag: TagCode.Model,
			type: TagType.ASCII,
			value: textEncoder.encode(metadata.cameraModel),
		})
	}

	if (metadata.artist) {
		TIFF.push({
			tag: TagCode.Artist,
			type: TagType.ASCII,
			value: textEncoder.encode(metadata.artist),
		})
	}

	if (metadata.copyright) {
		TIFF.push({
			tag: TagCode.Copyright,
			type: TagType.ASCII,
			value: textEncoder.encode(metadata.copyright),
		})
	}

	return {
		TIFF: TIFF,
		Exif: Exif,
		XMP: xmpData,
		ICCProfile: colorSpace === "display-p3" ? displayP3 : undefined,
	}
}

export const writeJpegMetadataToBytes = (
	jpegData: Uint8Array,
	metadata: PhotoMetadataRecord,
	pixelData: PixelData,
	ppi: number,
	xmpData: Record<string, any>,
) => {
	return writeJpegExifToBytes(jpegData, makeJpegMetadata(metadata, pixelData, ppi, xmpData))
}

export function finalizeJpegBytes(
	jpegData: Uint8Array,
	pixelData: PixelData,
	options: RawMetadataEncodeJPEGOptions,
) {
	if (options.metadata) {
		return writeJpegMetadataToBytes(
			jpegData,
			options.metadata,
			pixelData,
			options.ppi ?? 300,
			options.xmpData ?? {},
		)
	}

	if (pixelData.colorSpace === "display-p3") {
		return writeJpegExifToBytes(jpegData, {
			TIFF: [],
			Exif: [],
			XMP: {},
			ICCProfile: displayP3,
		})
	}

	return jpegData
}

export const writeJpegMetadataToBlob = async (
	blob: Blob,
	metadata: PhotoMetadataRecord,
	pixelData: PixelData,
	ppi: number,
	xmpData: Record<string, any>,
) => {
	return new Blob(
		[
			writeJpegMetadataToBytes(
				new Uint8Array(await blob.arrayBuffer()),
				metadata,
				pixelData,
				ppi,
				xmpData,
			),
		],
		{ type: blob.type },
	)
}
